/**
 * Copyright (c) S2 Partnership Ltd. 2021-2024
 *
 * A service that sets up the processing overlay native loading spinner
 *
 * @summary ProcessingOverlayService
 * @author S2
 *
 * Created at     : 17-06-2021
 */
import { Injectable } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { LoadingOptions } from '@ionic/core';
import { SyncModalComponent } from '../../common-ui/components/sync-modal/sync-modal.component';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class ProcessingOverlayService {
  /** Stores the HTML Ion Loading Element used for the current overlay. */
  private overlay: HTMLIonLoadingElement;
  /** Stores the modal for the new sync dialog window. */
  private modal: HTMLIonModalElement;
  /** The sync message to be displayed and emitted whenever it is updated. */
  private messageSubject = new BehaviorSubject<string>('');
  /**  The sync message stored as an observable. */
  public currentMessage$: Observable<string> = this.messageSubject.asObservable();
  /** The state of showing the cancel button in the sync modal and controls emitting the value when it is updated. */
  private canCancelSyncSubject = new BehaviorSubject<boolean>(false);
  /** The state of showing the cancel button stored as an observable. */
  public currentCanCancelSync$: Observable<boolean> = this.canCancelSyncSubject.asObservable();

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    public loadingController: LoadingController,
    public modalController: ModalController
    /* eslint-enable */
  ) {}


  /**
   * Sets up the loading controller overlay with appropriate options for the type of loading this is (i.e. sync or login).
   *
   * @arg {LoadingOptions} options The options for the loading controller. Should only define the message to be displayed. Other options are overwritten.
   * @arg {boolean} shouldEnableCancel Defines whether this overlay can be cancelled or not.
   * @return {Promise<any>} If successful then the created and displayed overlay is returned, otherwise null is returned.
   */
  async display(options: LoadingOptions = {}, shouldEnableCancel = false): Promise<any> {
    if (this.overlay) {
      console.error('Attempted to display an overlay when another one is already displayed');
      return;
    }
    let dismissOptions: LoadingOptions;
    if (options.message !== null && options.message !== undefined) {
      dismissOptions = {
        message: options.message,
        backdropDismiss: shouldEnableCancel
      };
    } else {
      dismissOptions = {
        backdropDismiss: shouldEnableCancel
      };
    }

    this.overlay = await this.loadingController.create(dismissOptions);
    await this.overlay.present();
    this.overlay.onDidDismiss().then(() => {
      this.overlay = null;
    });
    return this.overlay;
  }

  /**
   * Updates the message being displayed on the loading overlay with a new message.
   *
   * @arg {string} message The message to be displayed on the overlay.
   */
  updateMessage(message: string) {
    if (this.overlay) {
      this.overlay.message = message;
    } else {
      console.error('Tried to update an overlay message when no overlay is displayed');
    }
  }

  /**
   * Dismisses the overlay if it has not already been dismissed.
   */
  async dismiss() {
    if (!this.overlay) {
      console.error('Tried to dismiss an overlay that didnt exist');
      return;
    }

    await this.overlay.dismiss();
    this.overlay = null;
  }

  /**
   * Checks to see if there is an overlay being displayed or not.
   *
   * @return {boolean} True if there is an overlay being displayed.
   */
  isOverlayDisplayed(): boolean {
    if (!this.overlay) {
      return false;
    }
    return true;
  }

  /**
   * Checks to see if the overlay is unassigned, which means the smartsync service was cancelled.
   *
   * @return {boolean} True if there is no current overlay assigned.
   */
  public isCancelled(): boolean {
    if (this.overlay === null) {
      return true;
    }
    return false;
  }

  /**
   * Checks to see if there is a modal sync dialog being displayed.
   *
   * @return {boolean} True if the modal is cancelled.
   */
  public isModalCancelled(): boolean {
    // Treat both null and undefined as "no modal currently displayed"
    return !this.modal;
  }

  /**
   * Initialises and displays a sync dialog modal that cannot be dismissed with a background press.
   *
   * @return {void} Returns early without creating a modeal if there is already a modal being displayed.
   */
  public async displayModalLoading() {
    if (this.modal) {
      console.error('Attempted to display an overlay when another one is already displayed');
      return;
    }

    this.modal = await this.modalController.create({
        component: SyncModalComponent,
        componentProps: {
            syncMessage: '',
            modalDismiss: 'Cancel Sync'
        },
        id: 'sync-modal',
        canDismiss: async (_data, role) => {
            // Only allow dismissal if the dismissal type is not 'backdrop' touch
            return role !== 'backdrop';
      }
    });
    await this.modal.present();

    this.modal.onDidDismiss().then(() => {
      this.modal = null;
    });
  }

  /**
   * Dismisses the current sync dialog modal if there is one being displayed.
   *
   * @return {void} Returns early without dismissing if there is no modal to dismiss.
   */
  public async dismissLoadingModal() {
    if (!this.modal) {
      console.error('Tried to dismiss a loading modal that didnt exist');
      return;
    }

    await this.modal.dismiss();
    this.modal = null;
  }

  /**
   * Updates the current sync messge in the sync dialog modal.
   *
   * @arg {string} message The new message that should be displayed in the sync dialog modal.
   */
  async updateModalMessage(message: string) {
    this.messageSubject.next(message);
  }

  /**
   * Updated the current state of showing the cancel sync button in the sync dialog modal.
   *
   * @param canCancelSync The new status of showing the cancel sync button.
   */
  async updateCancelSync(canCancelSync: boolean) {
    this.canCancelSyncSubject.next(canCancelSync);
  }

}
